import React, {
  useCallback, useMemo
} from 'react';
import classNames from 'classnames';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import classes from '../../registration/classes.module.scss';
import withPGTranslation from '../../../config/withPGTranslation';

interface PhoneInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: string | null;
  helperText?: string;
  t: (key: string) => string;
  showHelperText?: boolean;
  inputClassName?: string;
  containerClassName?: string;
  smsSettings?: {
    allowedSmsCountries: string[];
    defaultCountry: string;
  }
}

/**
 * PhoneInputField component - Reusable phone number input with validation
 * Used in both registration and account settings
 */
function PhoneInputField({
  value,
  onChange,
  disabled = false,
  placeholder = '234-5678',
  error: externalError,
  helperText,
  showHelperText = true,
  inputClassName,
  containerClassName,
  smsSettings
}: PhoneInputFieldProps) {
  // Only show external error, no automatic validation on change
  const displayError = externalError || null;

  // Handle phone number change
  const handlePhoneChange = useCallback((phoneValue: string) => {
    onChange(phoneValue);
  }, [onChange]);

  // Memoize onlyCountries array to prevent unnecessary recalculations
  const onlyCountries = useMemo(() => smsSettings?.allowedSmsCountries?.map(code => code.toLowerCase()) ?? [], [smsSettings?.allowedSmsCountries]);

  // Memoize default country
  const defaultCountry = useMemo(() => smsSettings?.defaultCountry?.toLowerCase() ?? 'us', [smsSettings?.defaultCountry]);

  // Create a key that changes when smsSettings change to force PhoneInput re-render
  const phoneInputKey = useMemo(() => JSON.stringify({
    onlyCountries,
    defaultCountry,
    allowedSmsCountries: smsSettings?.allowedSmsCountries
  }), [onlyCountries, defaultCountry, smsSettings?.allowedSmsCountries]);

  return (
    <div className={ classNames(classes.phoneEntryInputWrapper, containerClassName) }>
      <div className={ classes.phoneEntryInputContainer }>
        <PhoneInput
          key={ phoneInputKey }
          value={ value }
          onChange={ handlePhoneChange }
          disabled={ disabled }
          placeholder={ placeholder }
          inputClass={ classNames(
            classes.phoneEntryInput,
            inputClassName,
            displayError ? classes.phoneEntryInputError : null
          ) }
          buttonClass={ classes.phoneEntryCountrySelector }
          containerClass={ classes.phoneEntryPhoneInputContainer }
          dropdownClass={ classes.phoneEntryCountryDropdown }
          searchClass={ classes.phoneEntryCountrySearch }
          enableSearch
          searchPlaceholder="Search country"
          disableSearchIcon
          countryCodeEditable={ false }
          specialLabel=""
          onlyCountries={ onlyCountries }
          country={ defaultCountry }
        />
        { displayError && (
          <div className={ classes.phoneLoginError }>
            { displayError }
          </div>
        ) }
      </div>
      { showHelperText && helperText && (
        <div className={ classes.phoneEntryHelperText }>
          {helperText}
        </div>
      ) }
    </div>
  );
}

export default withPGTranslation(PhoneInputField);
